/**
 * Remembers which folder the user chose for their shortcuts, so "Update" can
 * rewrite it in place instead of asking again every time.
 *
 * This is the codebase's only IndexedDB usage, and it has to be: a
 * `FileSystemDirectoryHandle` is a live browser object, not JSON, so plain
 * `localStorage` physically cannot hold one. IndexedDB stores it via
 * structured clone.
 *
 * Deliberately not generalized into a KV wrapper — one key, three functions.
 * If a second use for IndexedDB ever appears, that's the moment to extract
 * something shared, not now.
 *
 * Note the handle survives, but **the permission grant does not** — see
 * `ensureWritePermission` in `file-system-writer.ts`.
 */

const DB_NAME = 'matic-shortcuts';
const DB_VERSION = 1;
const STORE_NAME = 'handles';
const HANDLE_KEY_PREFIX = 'pages-folder';

// Scoped by tenant: a user who belongs to more than one MATIC tenant must not
// have Tenant A's chosen folder silently reused (and pruned into) while
// working in Tenant B.
function handleKey(tenantId: string): string {
  return `${HANDLE_KEY_PREFIX}:${tenantId}`;
}

async function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);

    request.addEventListener('upgradeneeded', () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    });

    request.addEventListener('success', () => {
      resolve(request.result);
    });

    request.addEventListener('error', () => {
      reject(request.error ?? new Error('Could not open the shortcuts store.'));
    });
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const database = await openDatabase();

  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = run(transaction.objectStore(STORE_NAME));

      request.addEventListener('success', () => {
        resolve(request.result);
      });

      request.addEventListener('error', () => {
        reject(request.error ?? new Error('Shortcuts store request failed.'));
      });
    });
  } finally {
    database.close();
  }
}

/**
 * The stored folder, or `undefined` if there isn't one. Storage being
 * unavailable (private browsing, disabled storage) is treated the same as
 * "none stored" — the user simply gets asked to pick a folder, which is a
 * working path, not an error worth surfacing.
 */
export async function getStoredDirectoryHandle(
  tenantId: string
): Promise<FileSystemDirectoryHandle | undefined> {
  try {
    return await withStore<FileSystemDirectoryHandle | undefined>(
      'readonly',
      (store) =>
        store.get(handleKey(tenantId)) as IDBRequest<
          FileSystemDirectoryHandle | undefined
        >
    );
  } catch {
    return undefined;
  }
}

export async function setStoredDirectoryHandle(
  tenantId: string,
  handle: FileSystemDirectoryHandle
): Promise<void> {
  try {
    await withStore('readwrite', (store) =>
      store.put(handle, handleKey(tenantId))
    );
  } catch {
    // Losing the handle only costs the user a re-pick next time. Not worth
    // failing a write that already succeeded on disk.
  }
}

export async function clearStoredDirectoryHandle(
  tenantId: string
): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.delete(handleKey(tenantId)));
  } catch {
    // Same reasoning as above.
  }
}
